import { watch, computed } from 'vue';
import { isProduction } from '../utils/env';
import type { PluginOptions } from './type';
import { useMenuStore, useSettingsStore, useUserStore } from '../stores';
import { type Router } from 'vue-router';

/** 神策配置 */
const sensorsConfig = computed(() => {
  const settingsStore = useSettingsStore();
  return settingsStore.settings.sensorsConfig;
});

/** 是否开启神策埋点 */
const isOpenSensors = computed(() => {
  return !!sensorsConfig.value?.system_code;
});

function getPageTitle(router?: Router) {
  const settingsStore = useSettingsStore();
  const menuStore = useMenuStore();
  const route = router?.currentRoute;
  const {
    system,
    route: { baseURL },
  } = settingsStore.settings;
  let { pathname } = window.location;
  if (baseURL) {
    pathname = pathname.replace(baseURL, '');
  }

  let $title = menuStore.menuPathMap[pathname]?.title || (route?.value.meta.title as string);
  $title = system.getDocumentTitle($title);
  return { $title };
}

export const onSensorsWebClick = (options: {
  title: string;
  source: string;
  menuLevel?: string;
  from?: { $url: string; $url_path: string };
  to?: { $url: string; $url_path: string };
}) => {
  sensorsConfig.value.onWebClick?.(options);
};

const options: PluginOptions = {
  async onVuePluginRegister({ router }) {
    if (!isOpenSensors.value) {
      return;
    }

    const { default: sensors } = await import('sa-sdk-javascript');
    const { isManualReport, initData = {} } = sensorsConfig.value;
    const menuStore = useMenuStore();

    // 兼容Promise及异步函数
    let { server_url, ...otherConfig } = initData;
    if (typeof server_url === 'function') {
      server_url = await server_url();
    } else if (server_url instanceof Promise) {
      server_url = await server_url;
    } else if (!server_url) {
      server_url = `https://ubs.sf-express.com/sa?project=${isProduction ? 'digital' : 'digitalgray'}`;
    }

    sensors.init({
      server_url,
      is_track_single_page: isManualReport ? false : () => getPageTitle(router), // 单页面配置，默认开启，若页面中有锚点设计，需要将该配置删除，否则触发锚点会多触发 $pageview 事件
      use_client_time: false,
      send_type: 'beacon',
      show_log: false,
      heatmap: {
        // 是否开启点击图，default 表示开启，自动采集 $WebClick 事件，可以设置 'not_collect' 表示关闭。
        clickmap: isManualReport ? 'not_collect' : 'default',
        // 是否开启触达图，not_collect 表示关闭，不会自动采集 $WebStay 事件，可以设置 'default' 表示开启。
        scroll_notice_map: isManualReport ? 'not_collect' : 'default',
        // 目前若开启全埋点支持任意层级 div 标签功能会发送1个404错误请求
        get_vtrack_config: false,
      },
      ...otherConfig,
    });

    // 页面刷新后采集
    if (!isManualReport) {
      watch(
        () => menuStore.menuPathMap,
        () => {
          sensors.quick('autoTrack', getPageTitle(router)); // 用于采集 $pageview 事件。
        },
      );
    }
  },

  async onUserDataUpdate({ operatorNo, operatorName, transitDepotNo, dutyName }) {
    // 神策
    if (!isOpenSensors.value) {
      return;
    }

    const {
      platform_type,
      platform_name,
      system_code,
      product_name,
      registerData = {},
      profileData
    } = sensorsConfig.value;
    const userStore = useUserStore();
    let config = {};
    if (typeof profileData === 'function') {
      config = await profileData(userStore.originUserInfo);
    } else {
      config = profileData || {};
    }

    const sensors = (window as any).sensorsDataAnalytic201505;
    sensors?.login(operatorNo);

    const empInfo = {
      emp_id: operatorNo,
      emp_name: operatorName,
      emp_role: dutyName,
      zone_code: transitDepotNo,
    }

    // 注册公共信息
    sensors.registerPage({ platform_type, platform_name, system_code, product_name, ...empInfo, ...registerData });
    sensors?.setProfile({
      uid: operatorNo,
      ...empInfo,
      ...config,
    });
  },
};

export default options;
